/* eslint-disable react-hooks/exhaustive-deps */
import {
  useContext, useState, useEffect, useReducer, useRef
} from 'react';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useProductDeprecated, useIRGDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';

const domains = ['$ProductPod'];

export const ACTION_TYPES = {
  LOAD_RESPONSE: 'LOAD_RESPONSE',
  LOAD_MORE: 'LOAD_MORE',
  LOAD_QUEUE: 'LOAD_QUEUE',
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  ADD_CATEGORY_IMAGES: 'ADD_CATEGORY_IMAGES',
  ADD_MOBILE_CARD_IMAGES: 'ADD_MOBILE_CARD_IMAGES',
  REMOVE_ITEM: 'REMOVE_ITEM'
};

const initialState = {
  tabs: null,
  active: null,
  errorItems: [],
};

const createAllItems = ({ categories, products }) => {
  const set = new Set();
  if (categories) {
    (categories || []).forEach((cat) => {
      (cat.itemIds || []).forEach((itemId) => set.add(itemId));
    });
  } else {
    (products || []).forEach(({ product }) => {
      const itemId = product?.identifiers?.itemId;
      set.add(itemId);
    });
  }
  return Array.from(set);
};

const buildAllItemsArray = (categoryArray) => {
  const firstProductInEachCat = [];
  let restOfProductsInScatteredOrder = [];
  let lastIndexInserted = 0;
  let itemIds;
  categoryArray.forEach((category, categoryIndex) => {
    itemIds = [...category?.itemIds];
    if (itemIds) {
      firstProductInEachCat.push(itemIds.shift());
      if (categoryIndex === 0) {
        restOfProductsInScatteredOrder = restOfProductsInScatteredOrder.concat(itemIds);
      } else {
        itemIds.forEach((itemId, itemIndex) => {
          lastIndexInserted += categoryIndex + 1 + itemIndex;
          restOfProductsInScatteredOrder.splice(lastIndexInserted, 0, itemId);
        });
      }
    }
  });

  return firstProductInEachCat.concat(restOfProductsInScatteredOrder);
};

const buildMobileCardImageUrls = (products) => {
  if (!products || !products.length) {
    return null;
  }

  return products.slice(0, 4).map((product, index) => {
    return product?.product?.media?.image?.url?.replace('1000', '300');
  });
};

// filter out sample swatch
const getFilteredData = (productData, data) => {
  let filteredData;
  const sampleId = productData?.product?.identifiers?.sampleId;
  if (sampleId) {
    const { irg } = data?.dynamicRecs || {};
    const irgArr = [irg.accessories, irg.collection, irg.coordinatingItems];

    const filteredIrgArr = (irgArr || []).map((bundle) => {
      const bundleCategories = bundle?.categories;
      const bundleProducts = bundle?.products;

      const tempCategories = bundleCategories
        ? bundleCategories.map((category) => {
          const filteredItemIds = (category?.itemIds || [])
            .filter((itemId) => itemId !== sampleId);
          return {
            ...category,
            itemIds: filteredItemIds
          };
        }).filter((category) => {
          return category?.itemIds?.length > 0;
        })
        : null;
      const filteredCategories = tempCategories && tempCategories.length > 0
        ? tempCategories
        : null;

      const tempProducts = bundleProducts
        ? bundleProducts.filter((product) => product?.product?.itemId !== sampleId)
        : null;
      const filteredProducts = tempProducts && tempProducts.length > 0
        ? tempProducts
        : null;

      return {
        ...bundle,
        categories: filteredCategories,
        products: filteredProducts
      };
    });

    filteredData = {
      ...data,
      dynamicRecs: {
        ...data.dynamicRecs,
        irg: {
          ...data.dynamicRecs.irg,
          accessories: filteredIrgArr[0],
          collection: filteredIrgArr[1],
          coordinatingItems: filteredIrgArr[2]
        }
      }
    };
  } else {
    filteredData = data;
  }
  return filteredData;
};

const setCategoryImages = ({ state, action }) => {
  return {
    ...state,
    tabs: state.tabs.map((tab) => {
      if (tab.isParent) {
        return tab;
      }

      const indexOfCurrentItem = action?.value?.findIndex((responseItem) => {
        return responseItem?.itemId === tab.itemIds?.[0];
      });
      return {
        ...tab,
        categoryImage: action?.value?.[indexOfCurrentItem]?.media?.image.url
      };
    })
  };
};

const loadResponse = ({ state, action }) => {
  const filteredData = getFilteredData(action.productData, action.data);
  const response = filteredData.dynamicRecs?.irg || {};
  const keys = Object.keys(response).filter((key) => !/^__/.test(key));

  const { imageData } = action;
  // default tab
  let tabs = (state.tabs || []).slice();

  tabs = keys.map((key, index) => {
    return {
      name: key,
      isParent: true,
      active: index === 0,
      storeId: action.storeId,
      hasSubcategories: !!response[key]?.categories?.length,
      itemIds: createAllItems({
        categories: response[key]?.categories,
        products: response[key]?.products
      }),
      visibleItemIds: response[key]?.products?.map(({ product }) => product?.identifiers?.itemId),
      mobileCardImageUrls: buildMobileCardImageUrls(response[key]?.products)
    };
  });

  if (tabs?.[0]) {
    tabs[0].brandName = response?.collection?.products?.[0]?.product?.identifiers?.brandName;
  }

  // sub categories
  keys.forEach((key, keyIndex) => {
    const tab = response[key];
    let subCategories = [];

    if (tab && tab.categories && tab.categories.length) {
      const allItems = buildAllItemsArray(tab.categories);
      const categoryImageIds = allItems.slice(0, tab.categories.length + 1);

      if (categoryImageIds && keyIndex === 0 && tabs?.[0]) {
        tabs[0].categoryImageIds = categoryImageIds;
      }

      let allItemsCategory;
      tab.categories.forEach((category) => {
        subCategories.push({
          subCategory: `${category.category}`,
          name: key,
          isParent: false,
          itemIds: category.itemIds,
          visibleItemIds: []
        });
      });

      allItemsCategory = {
        subCategory: 'All Items',
        name: key,
        storeId: action.storeId,
        active: keyIndex === 0,
        isParent: false,
        itemIds: allItems,
        visibleItemIds: tab.products?.map(({ product }) => product?.identifiers?.itemId)
      };
      subCategories.unshift(allItemsCategory);

      if (allItemsCategory.visibleItemIds && tab.visibleItemIds) {
        tab.visibleItemIds = tab.visibleItemIds.concat(allItemsCategory.slice(4));
      }
      tabs = tabs.concat(subCategories);
    }
  });

  const defaultActive = state.active
    ? state.active
    : tabs?.[0]?.name;

  let ret = {
    ...state,
    storeId: action.storeId || state.storeId,
    tabs,
    response,
    active: defaultActive,
  };
  if (imageData) {
    ret = setCategoryImages({ state: ret, action: { value: imageData?.products } });
  }
  return ret;
};

const loadProductsForTab = ({ state }) => {
  return state;
};

const loadVisibleItems = ({ state, action }) => {
  const { limit = 8, start = 0, tab: tabName, subCategory } = action;
  const stateTab = state?.tabs?.find((tabObj) => tabObj.name === tabName && tabObj.subCategory === action.subCategory);
  const len = Math.min(start + limit, stateTab.itemIds.length);

  const itemIds = stateTab?.itemIds?.slice(start, len);

  const tabs = (state.tabs || []).map((tab) => {
    if (tab.name === tabName
      && (action.subCategory === tab.subCategory || tab.isParent || tab.subCategory === 'All Items')) {
      const visibleItemIds = tab.visibleItemIds?.slice() || [];
      visibleItemIds.splice(start, len, ...itemIds);
      return {
        ...tab,
        visibleItemIds,
      };
    }

    return tab;
  });

  return {
    ...state,
    tabs
  };
};

const setActiveTabState = ({ state, action }) => {
  return {
    ...state,
    tabs: state.tabs.map((tab) => {
      if (tab.name === action.tab) {
        if (tab.isParent) {
          return {
            ...tab,
            active: true
          };
        }

        if (tab.subCategory === action.subCategory) {
          return {
            ...tab,
            active: true
          };
        }

        return {
          ...tab,
          active: false
        };
      }

      return tab;
    })
  };
};

const setMobileCardImages = ({ state, action }) => {
  return {
    ...state,
    tabs: state.tabs.map((tab) => {
      if (tab.isParent) {
        return tab;
      }
      const indexOfCurrentItem = action?.value?.findIndex((responseItem) => {
        return responseItem?.itemId === tab.itemIds?.[0];
      });

      return {
        ...tab,
        mobileImageUrls: action?.value?.[indexOfCurrentItem]?.product?.media?.image?.url?.replace(
          '1000', action?.value?.[indexOfCurrentItem]?.product?.media?.image?.sizes?.[0]
        )
      };
    })
  };
};

const removeItemFromDataset = ({ state, action }) => {

  const exists = (state.errorItems || []).indexOf(action.itemId) > -1;

  const errorItems = [
    ...state.errorItems
  ];
  if (!exists) {
    errorItems.push(action.itemId);
  }
  return {
    ...state,
    errorItems
  };
};

function reducer(state, action) {

  switch (action.type) {
    case ACTION_TYPES.LOAD_QUEUE:
      return loadVisibleItems({ state, action });
    case ACTION_TYPES.LOAD_RESPONSE:
      return loadResponse({ state, action });
    case ACTION_TYPES.LOAD_MORE_PRODUCTS_FOR_TAB:
      return loadProductsForTab({ state, action });
    case ACTION_TYPES.SET_ACTIVE_TAB:
      return setActiveTabState({ state, action });
    case ACTION_TYPES.ADD_CATEGORY_IMAGES:
      return setCategoryImages({ state, action });
    case ACTION_TYPES.ADD_MOBILE_CARD_IMAGES:
      return setMobileCardImages({ state, action });
    case ACTION_TYPES.REMOVE_ITEM:
      return removeItemFromDataset({ state, action });
    default:
      throw new Error('no reducer found');
  }
}

const getDefaultStartForTab = ({ state, tab }) => {
  const tabObject = state?.tabs?.find(({ name }) => name === tab);
  return tabObject?.visibleItemIds?.length;
};

const filterErrorItems = ({ itemIds = [], errorItems = [] }) => {
  return (itemIds || []).filter((itemId) => errorItems.indexOf(itemId) === -1);
};

export const useIRG = ({ itemId, maxItems, accordion }) => {
  const {
    channel,
    clientStore,
  } = useContext(ExperienceContext);

  const [state, dispatch] = useReducer(reducer, initialState);

  const storeId = useStoreId();

  // call to get sampleId
  let { data: productData, error: errState } = useProductDeprecated({
    itemId,
    domains: ['identifiers'],
  }, 'product');

  const { data, loading, error, variables } = useIRGDeprecated({
    itemId,
    storeId,
    maxItems,
    domains,
    ssr: true
  });

  const categoryImageIds = state.tabs?.[0]?.categoryImageIds || [];
  const [makeItemIdsRequest, {
    data: imageData,
    loading: imageLoading,
    error: imageError
  }] = useProductDeprecated({
    itemIds: categoryImageIds,
    // ac3 has bug with skip, using lazy for now
    lazy: true,
    // skip: !categoryImageIds.length || channel === 'mobile',
    domains: ['mediaSlim'],
    ssr: false
  }, 'products');

  useEffect(() => {
    if (categoryImageIds?.length && channel !== 'mobile' && state?.storeId !== '8119') {
      makeItemIdsRequest();
    }
  }, [categoryImageIds?.length, state?.storeId]);

  useEffect(() => {
    if (imageData?.products?.length) {
      dispatch({ type: ACTION_TYPES.ADD_CATEGORY_IMAGES, value: imageData?.products });
    }
  }, [imageData]);

  // updating reducer state with response
  useEffect(() => {
    if (data && !loading) {
      dispatch({
        type: ACTION_TYPES.LOAD_RESPONSE, data, productData, storeId: variables.storeId, imageData
      });
    }
  }, [data?.dynamicRecs?.__typename, loading, itemId]);

  const loadMoreForTab = (props) => {
    if (typeof props === 'function') {
      const result = props(state.tabs);
      if (result && typeof result === 'object') {
        let { start, limit, tab } = result;
        if (!start) {
          start = getDefaultStartForTab({ state, tab });
        }
        dispatch({ type: ACTION_TYPES.LOAD_QUEUE, tab, start, limit });
      }
    } else {
      let { tab, start, limit, subCategory } = props;
      if (typeof start === 'undefined') {
        start = getDefaultStartForTab({ state, tab });
      }
      dispatch({
        type: ACTION_TYPES.LOAD_QUEUE, tab, start, limit, subCategory
      });
    }
  };

  const loadSubCategory = ({ tab, subCategory }) => {
    dispatch({
      type: ACTION_TYPES.LOAD_QUEUE,
      tab,
      subCategory,
    });
  };

  const getPublicState = () => {
    let tabs = state.tabs;
    if (!tabs && data) {
      // initialize the state, we cant wait for useEffect
      // to render the load_response change to update state cause
      // that does not happen ssr side.
      const action = {
        type: ACTION_TYPES.LOAD_RESPONSE,
        data,
        storeId: '8119'
      };
      const preloadedState = reducer(state, action);
      ({ tabs } = preloadedState);
    }
    const parents = (tabs || [])
      .filter((tab) => tab.isParent)
      .map((parent) => {
        const ret = {
          ...parent,
          storeId: state.storeId || '8119',
          categories: tabs.filter((tab) => !tab.isParent && tab.name === parent.name),
          itemIds: filterErrorItems({ itemIds: parent.itemIds, errorItems: state.errorItems }),
          visibleItemIds: filterErrorItems({ itemIds: parent.visibleItemIds, errorItems: state.errorItems })
        };

        return ret;
      });
    return {
      ...state,
      ready: !!state?.tabs?.length && state.active,
      tabs: parents || []
    };
  };

  const setActiveTab = ({ tab, subCategory }) => {
    dispatch({ type: ACTION_TYPES.SET_ACTIVE_TAB, tab, subCategory });
  };

  const getActiveTab = () => {
    return state.active;
  };

  const removeItem = (id) => {
    if (id) {
      dispatch({ type: ACTION_TYPES.REMOVE_ITEM, itemId: id });
    }
  };

  return [getPublicState(), {
    loadMoreForTab,
    loadSubCategory,
    setActiveTab,
    getActiveTab,
    removeItem
  }];
};
